import { LogEvent } from "../../utils/LogEvent";

const GooglePayScriptUrl = "https://pay.google.com/gp/p/js/pay.js";

let loadPromise: Promise<boolean> | null = null;

/**
 * Ensures the Google Pay API script is loaded and ready.
 * If the script is already present, resolves immediately.
 * Otherwise, injects the script and waits for it to load.
 * Returns true if the API is available, false otherwise.
 */
export function LoadGooglePayScript(): Promise<boolean> {

    LogEvent.GooglePayDebug("Trying to Load Google Pay script");

    if (loadPromise) {
        LogEvent.GooglePayDebug("Google Pay script Already Loaded");
        return loadPromise;
    }

    loadPromise = new Promise<boolean>((resolve) => {
        if (typeof window === "undefined") {
            LogEvent.GooglePayDebug("Window is undefined");
            resolve(false);
            return;
        }

        if (isGooglePayApiAvailable()) {
            LogEvent.GooglePayDebug("Google Pay API Available");
            resolve(true);
            return;
        }

        const existingScript = document.querySelector(`script[src="${GooglePayScriptUrl}"]`) as HTMLScriptElement | null;

        if (existingScript) {
            LogEvent.GooglePayDebug("Google Pay script tag exists, waiting for API");
            waitForGooglePayApi(() => {
                const available = isGooglePayApiAvailable();
                LogEvent.GooglePayDebug(available ? "Google Pay API became available" : "Google Pay API did not become available after waiting");
                resolve(available);
            });
            return;
        }

        LogEvent.GooglePayDebug("Injecting Google Pay script");

        const script = document.createElement("script");
        script.src = GooglePayScriptUrl;
        script.async = true;

        script.onload = () => {
            const available = isGooglePayApiAvailable();
            LogEvent.GooglePayDebug(available ? "Google Pay script loaded and API available" : "Google Pay script loaded but API not available");
            resolve(available);
        };

        script.onerror = () => {
            LogEvent.GooglePayDebug("Google Pay script failed to load (possible content blocker or network issue)");
            resolve(false);
        };

        document.head.appendChild(script);
    });

    return loadPromise;
}

function isGooglePayApiAvailable(): boolean {
    return typeof google !== "undefined" && !!google.payments && !!google.payments.api;
}

function waitForGooglePayApi(onReady: () => void): void {
    const maxAttempts = 100;
    let attempts = 0;

    const interval = window.setInterval(() => {
        attempts++;

        if (isGooglePayApiAvailable() || attempts >= maxAttempts) {
            clearInterval(interval);
            onReady();
        }
    }, 100);
}
